// 🎯 KONFIGURACJA OGRODZEŃ - Wspólny plik dla FenceSelector i ThreeJSCanvas3D

export interface FenceElementConfig {
  groups: string[]
  textures: Record<string, {
    type: 'wood' | 'metal'
    diffuse: string
    normal?: string
    roughness?: string
    metalness?: string
    ao?: string
  }>
  defaultHeight?: number
  spacing?: number
  positioning?: Record<string, { y: number }>
  rotation?: { x: number; y: number; z: number }
  blocksCount?: number // 📦 Liczba zwykłych bloczków w elemencie
  decorationBlocksCount?: number // 🎨 Liczba bloczków dekoracyjnych w elemencie
  decorationBlockHeight?: number // 📐 Wysokość bloczka dekoracyjnego (cm)
  metalPrice?: number // 💰 Cena za metal na przęsło (zł brutto)
  basePrice?: number // 💰 Cena bazowa za element (zł brutto) - dla bram i drzwi
}

export interface FenceConfig {
  id: string
  name: string
  category: string
  dimensions: string
  colors: string[]
  modelPath: string
  texturePath: string
  basePrice?: number // 💰 Cena bazowa za przęsło (zł brutto) - DEPRECATED, użyj blockPrice
  blockPrice?: number // 💰 Cena pojedynczego bloczka zwykłego (zł brutto)
  decorationBlockPrice?: number // 🎨 Cena pojedynczego bloczka dekoracyjnego (zł brutto) - domyślna
  decorationBlockDimensions?: string // 📐 Wymiary bloczka dekoracyjnego (np. "100×20×30") - jeśli różne od zwykłego
  blockHeight?: number // 📐 Wysokość pojedynczego bloczka (cm)
  colorPrices?: Record<string, {
    blockPrice?: number
    decorationBlockPrice?: number
  }> // 🎨 Ceny zależne od koloru
  // 🎯 DEBUG: Wymuszenie odświeżenia cache - timestamp: 1760310441330
  colorTextures?: Record<string, {
    colorName?: string
    materialTexture: string | string[]
    aluminumTexture: string
    thumbnail?: string
    modelPath?: string
  }>
  elements: {
    post?: FenceElementConfig
    section?: FenceElementConfig
    gate?: FenceElementConfig
    door?: FenceElementConfig
  }
  materials?: {
    wood: {
      roughness: number
      metalness: number
      envMapIntensity: number
      side: 'FrontSide' | 'DoubleSide'
      flatShading: boolean
    }
    metal: {
      roughness: number
      metalness: number
      envMapIntensity: number
      side: 'FrontSide' | 'DoubleSide'
      flatShading: boolean
    }
  }
  autoSection?: {
    enabled: boolean
    delay: number
    position: 'center' | 'left' | 'right'
  }
  camera?: {
    minDistance: number
    zoomSpeed: number
    minPolarAngle: number
    maxPolarAngle: number
  }
  // 🎯 Warianty dla "Pełne" ogrodzeń (np. Łupane Muro)
  availableVariants?: string[] // Lista dostępnych wariantów (np. ['p22', 'p38', 's22', 's38'])
  variantConfigs?: Record<string, string> // Mapowanie wariant -> config ID (np. { 'p22': 'lupane-muro-p22' })
}

// 🎯 FUNKCJE POMOCNICZE: Tworzenie konfiguracji
export const createElementConfig = (
  groups: string[],
  textures: Record<string, any>,
  defaultHeight?: number,
  spacing?: number,
  positioning?: Record<string, { y: number }>,
  rotation?: { x: number; y: number; z: number }
): FenceElementConfig => ({
  groups,
  textures,
  ...(defaultHeight && { defaultHeight }),
  ...(spacing && { spacing }),
  ...(positioning && { positioning }),
  rotation: rotation || { x: 1.5708, y: 3.1416, z: 0 }
})

export const createTextureConfig = (
  type: 'wood' | 'metal',
  diffuse: string,
  normal?: string,
  roughness?: string,
  metalness?: string,
  ao?: string
) => ({
  type,
  diffuse,
  ...(normal && { normal }),
  ...(roughness && { roughness }),
  ...(metalness && { metalness }),
  ...(ao && { ao })
})

export const createFenceConfig = (
  id: string,
  name: string,
  category: string,
  dimensions: string,
  colors: string[],
  modelPath: string,
  texturePath: string,
  elements: any,
  materials?: any,
  autoSection?: any,
  camera?: any,
  basePrice?: number,
  blockPrice?: number,
  blockHeight?: number,
  decorationBlockPrice?: number
): FenceConfig => ({
  id,
  name,
  category,
  dimensions,
  colors,
  modelPath,
  texturePath,
  basePrice,
  blockPrice,
  blockHeight,
  decorationBlockPrice,
  elements,
  materials: materials || {
    wood: { roughness: 0.8, metalness: 0.1, envMapIntensity: 1.0, side: 'DoubleSide', flatShading: false },
    metal: { roughness: 0.3, metalness: 0.9, envMapIntensity: 1.2, side: 'DoubleSide', flatShading: false }
  },
  autoSection: autoSection || { enabled: true, delay: 200, position: 'center' },
  camera: camera || { minDistance: 2.0, zoomSpeed: 0.5, minPolarAngle: 0, maxPolarAngle: 1.5708 }
})

// 🎯 KATALOG OGRODZEŃ
export const fenceCatalog: FenceConfig[] = [
  // GŁADKIE
  createFenceConfig(
    'gladkie_max',
    'Gładkie Max',
    'gladkie',
    '120×25×40',
    ['Biały', 'Szary', 'Grafit', 'A12 Frappe'],
    '/Ogrodzenie/Ogrodzenia-gladkie-Max/Ogrodzenie-gladkie-Max-szary/Ogrodzenie-gladkie-Max-szary.dae',
    '/Ogrodzenie/Ogrodzenia-gladkie-Max/Ogrodzenie-gladkie-Max-szary/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        6,
        0.65
      ),
      section: createElementConfig(
        ['group_13', 'group_14', 'group_15', 'group_16'],
        {
          'group_13': createTextureConfig('metal', 'Aluminum.jpg'),
          'group_14': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_15': createTextureConfig('wood', 'material_1.jpg'),
          'group_16': createTextureConfig('wood', 'material_1_0.jpg')
        },
        undefined,
        undefined,
        {
          'group_13': { y: 0.09 },
          'group_14': { y: 0.0 },
          'group_15': { y: 0.1 },
          'group_16': { y: 0.2 }
        }
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    381.30, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    381.30, // 💰 Cena pojedynczego bloczka (zł brutto)
    25, // 📐 Wysokość bloczka (cm)
    381.30 // 🎨 Cena bloczka dekoracyjnego (zł brutto)
  ),
  
  createFenceConfig(
    'gladkie_long',
    'Gładkie Long',
    'gladkie',
    '100×20×30',
    ['Biały', 'Szary', 'Grafit A6 Marago', 'A12 Frappe'],
    '/Ogrodzenie/Ogrodzenia-gladkie-LONG/Ogrodzenie-gladkie-LONG-szary/Ogrodzenie-gladkie-LONG-szary.dae',
    '/Ogrodzenie/Ogrodzenia-gladkie-LONG/Ogrodzenie-gladkie-LONG-szary/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        6,
        1.95
      ),
      section: createElementConfig(
        ['group_13', 'group_14', 'group_15', 'group_16'],
        {
          'group_13': createTextureConfig('metal', 'Aluminum.jpg'),
          'group_14': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_15': createTextureConfig('wood', 'material_1.jpg'),
          'group_16': createTextureConfig('wood', 'material_1_0.jpg')
        },
        undefined,
        undefined,
        {
          'group_13': { y: 0.09 },
          'group_14': { y: 0.0 },
          'group_15': { y: 0.1 },
          'group_16': { y: 0.2 }
        }
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    255.72, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    255.72, // 💰 Cena pojedynczego bloczka (zł brutto)
    20, // 📐 Wysokość bloczka (cm)
    255.72 // 🎨 Cena bloczka dekoracyjnego (zł brutto)
  ),
  
  // 🎯 GLADKIE MEDIUM - PEŁNA KONFIGURACJA (przykład)
  createFenceConfig(
    'gladkie_medium',
    'Gładkie Medium',
    'gladkie',
    '80×20×25',
    ['Biały', 'Szary', 'Grafit', 'Latte', 'A6 Marago', 'A12 Frappe'],
    '/Ogrodzenie/gladkie_medium_dae/Ogrodzenei-gladkie-Medium---konfigurator.dae',
    '/Ogrodzenie/gladkie_medium_dae',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        6,
        0.16
      ),
      section: createElementConfig(
        ['group_13', 'group_14', 'group_15', 'group_16'],
        {
          'group_13': createTextureConfig('metal', 'Aluminum.jpg'),
          'group_14': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_15': createTextureConfig('wood', 'material_1.jpg'),
          'group_16': createTextureConfig('wood', 'material_1_0.jpg')
        },
        undefined,
        undefined,
        {
          'group_13': { y: 0.09 },
          'group_14': { y: 0.0 },
          'group_15': { y: 0.1 },
          'group_16': { y: 0.2 }
        }
      ),
      gate: createElementConfig(
        ['group_18', 'group_19'],
        {
          'group_18': createTextureConfig('metal', 'Aluminum.jpg'),
          'group_19': createTextureConfig('metal', 'Aluminum.jpg')
        }
      ),
      door: createElementConfig(
        ['group_17'],
        {
          'group_17': createTextureConfig('wood', 'material_1.jpg')
        }
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    127.86, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    127.86, // 💰 Cena pojedynczego bloczka (zł brutto)
    20, // 📐 Wysokość bloczka (cm)
    127.86 // 🎨 Cena bloczka dekoracyjnego (zł brutto)
  ),
  
  // SLIM
  createFenceConfig(
    'slim_3d',
    'Slim 3D',
    'slim',
    '25x25, 26,5x25, 28x25, 25x27, 26,5x37,5, 28x75, 26,5x75',
    ['Biały', 'Grafit', 'A6 Marago', 'A12 Frappe'],
    '/Ogrodzenie/Ogrodzenei gładkie Medium - konfigurator.dae',
    '/Ogrodzenie/textures/slim_3d/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        4, // Slim ma mniejszą wysokość
        0.5 // Mniejszy odstęp dla slim
      ),
      section: createElementConfig(
        ['group_13', 'group_14', 'group_15'],
        {
          'group_13': createTextureConfig('metal', 'Aluminum.jpg'),
          'group_14': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_15': createTextureConfig('wood', 'material_1.jpg')
        },
        undefined,
        undefined,
        {
          'group_13': { y: 0.08 },
          'group_14': { y: 0.0 },
          'group_15': { y: 0.08 }
        }
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    226.04, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    226.04, // 💰 Cena pojedynczego bloczka (zł brutto)
    32, // 📐 Wysokość bloczka (cm) - Slim 3D ma 100×32
    389.75 // 🎨 Cena bloczka dekoracyjnego (zł brutto) - średnia z 385.05 i 394.44
  ),
  
  createFenceConfig(
    'slim',
    'Slim',
    'slim',
    '60×25×12,5',
    ['Biały', 'Grafit', 'Latte', 'Lungo', 'Espresso', 'A4 Czekolada', 'A5 Toffi', 'A6 Marago', 'A6 S', 'A7 Wanilia', 'A8 Mocca', 'A11 Cappucino', 'A12 Frappe'],
    '/Ogrodzenie/Ogrodzenei gładkie Medium - konfigurator.dae',
    '/Ogrodzenie/textures/slim/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        4,
        0.5
      ),
      section: createElementConfig(
        ['group_13', 'group_14', 'group_15'],
        {
          'group_13': createTextureConfig('metal', 'Aluminum.jpg'),
          'group_14': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_15': createTextureConfig('wood', 'material_1.jpg')
        },
        undefined,
        undefined,
        {
          'group_13': { y: 0.08 },
          'group_14': { y: 0.0 },
          'group_15': { y: 0.08 }
        }
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    53.30, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    53.30, // 💰 Cena pojedynczego bloczka (zł brutto)
    25, // 📐 Wysokość bloczka (cm)
    85.95 // 🎨 Cena bloczka dekoracyjnego (zł brutto) - bazowa dla grafit, A4-A8
  ),
  
  // ROYAL S 50
  createFenceConfig(
    'royal_s50',
    'Royal S 50',
    'royal',
    '50x40x20',
    ['Biały', 'Grafit', 'Latte', 'Lungo', 'Espresso', 'A4 Czekolada', 'A5 Toffi', 'A6 Marago', 'A6 S', 'A7 Wanilia', 'A8 Mocca', 'A10 Romano', 'A12 Frappe'],
    '/Ogrodzenie/Ogrodzenie-gladkie-Royal/Ogrodzenie-gladkie-Royal-biale/Ogrodzenie-gladkie-Royal-biale.dae',
    '/Ogrodzenie/Ogrodzenie-gladkie-Royal/Ogrodzenie-gladkie-Royal-biale/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1.png'),
          'group_1': createTextureConfig('wood', 'material_1.png')
        },
        6,
        0.5
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    41.93, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    41.93, // 💰 Cena pojedynczego bloczka (zł brutto) - bazowa dla biały, grafit, A4-A8
    20, // 📐 Wysokość bloczka (cm)
    51.57 // 🎨 Cena bloczka dekoracyjnego (zł brutto) - bazowa dla biały, grafit, A4-A8
  ),
  
  // ŁUPANE MURO
  createFenceConfig(
    'lupane-muro',
    'Łupane Muro',
    'lupane',
    'Wieloformatowe',
    ['Biały Forte', 'Biały', 'Fredo', 'Grafit', 'Latte', 'Lungo', 'A1 Złota Jesień', 'A3 Złota Jesień', 'A4 Czekolada', 'A5 Toffi', 'A6 Marago', 'A7 Wanilia', 'A8 Mocca', 'A11 Cappucino', 'A12 Frappe'],
    '/Ogrodzenia-NEW/Ogrodzenia Łupane Muro/Ogrodzenie Muro biały/Ogrodzenie Muro biały.dae',
    '/Ogrodzenia-NEW/Ogrodzenia Łupane Muro/Ogrodzenie Muro biały/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        6,
        0.7
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    30.00, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    30.00, // 💰 Cena pojedynczego bloczka (zł brutto)
    16, // 📐 Wysokość bloczka (cm) - z wymiarów 38×22×16 lub 38×38×16
    30.00 // 🎨 Cena bloczka dekoracyjnego (zł brutto)
  ),
  
  // PŁYTY ELEWACYJNE
  createFenceConfig(
    'plyta_elewacyjna',
    'Płyta Elewacyjna',
    'plyty',
    '8x38, 12x38, 16x38',
    ['Biały Forte', 'Biały', 'Fredo', 'Grafit', 'Lungo', 'Latte', 'A4 Czekolada', 'A5 Toffi', 'A6 Marago', 'A7 Wanilia', 'A8 Mocca', 'A11 Cappucino', 'A12 Frappe'],
    '/Ogrodzenie/Ogrodzenei gładkie Medium - konfigurator.dae',
    '/Ogrodzenie/textures/płyta_elewacyjna/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        3,
        0.4
      )
    }
  ),
  
  // GAZON
  createFenceConfig(
    'gazon',
    'Gazon',
    'gazon',
    '34,5x27,8, 50x39,5',
    ['Szary', 'Grafit', 'Brąz', 'Czerwony', 'A1 Złota Jesień', 'A4 Czekolada', 'A5 Toffi', 'A6 Marago', 'A8 Mocca'],
    '/Ogrodzenie/Ogrodzenei gładkie Medium - konfigurator.dae',
    '/Ogrodzenie/textures/gazon/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        4,
        0.5
      )
    }
  ),
  
  // DASZKI
  createFenceConfig(
    'daszek_gładki_royal',
    'Daszek Gładki Royal',
    'daszki',
    '40x50, 20x50',
    ['Biały', 'Grafit', 'Latte', 'Lungo', 'Espresso', 'A4 Czekolada', 'A5 Toffi', 'A6 Marago', 'A6 S', 'A7 Wanilia', 'A8 Mocca', 'A10 Romano', 'A11 Cappucino', 'A12 Frappe'],
    '/Ogrodzenie/Ogrodzenei gładkie Medium - konfigurator.dae',
    '/Ogrodzenie/textures/daszek_royal/',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        3,
        0.4
      )
    }
  ),
  
  // VISION
  createFenceConfig(
    'vision-bet-long',
    'Vision-Bet Long',
    'vision',
    '100×20×30',
    ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25'],
    '/Ogrodzenia-NEW/Ogrodzenia Vision-Bet Long/Ogrodzenie Vision-Bet LONG 01/Ogrodzenie Vision-Bet LONG 01.dae',
    '/Ogrodzenia-NEW/Ogrodzenia Vision-Bet Long/Ogrodzenie Vision-Bet LONG 01',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        6,
        0.65
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    281.00, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    281.00, // 💰 Cena pojedynczego bloczka (zł brutto)
    20, // 📐 Wysokość bloczka (cm)
    281.00 // 🎨 Cena bloczka dekoracyjnego (zł brutto)
  ),
  
  createFenceConfig(
    'vision-bet-max',
    'Vision-Bet Max',
    'vision',
    '120×25×40',
    ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25'],
    '/Ogrodzenia-NEW/Ogrodzenia Vision-Bet Max/Ogrodzenie Vision-Bet Max 01/Ogrodzenie Vision-Bet Max 01.dae',
    '/Ogrodzenia-NEW/Ogrodzenia Vision-Bet Max/Ogrodzenie Vision-Bet Max 01',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        7,
        0.8
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    412.05, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    412.05, // 💰 Cena pojedynczego bloczka (zł brutto)
    25, // 📐 Wysokość bloczka (cm)
    412.05 // 🎨 Cena bloczka dekoracyjnego (zł brutto)
  ),
  
  createFenceConfig(
    'vision-bet-medium',
    'Vision-Bet Medium',
    'vision',
    '80×20×25',
    ['01', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25'],
    '/Ogrodzenia-NEW/Ogrodzenia Vision-Bet Medium/Ogrodzenie Vision-Bet Medium 01/Ogrodzenie Vision-Bet Medium 01.dae',
    '/Ogrodzenia-NEW/Ogrodzenia Vision-Bet Medium/Ogrodzenie Vision-Bet Medium 01',
    {
      post: createElementConfig(
        ['group_0', 'group_1'],
        {
          'group_0': createTextureConfig('wood', 'material_1_0.jpg'),
          'group_1': createTextureConfig('wood', 'material_1.jpg')
        },
        6,
        0.65
      )
    },
    undefined, // materials (default)
    undefined, // autoSection (default)
    undefined, // camera (default)
    153.75, // 💰 Cena bazowa (zł brutto) - DEPRECATED
    153.75, // 💰 Cena pojedynczego bloczka (zł brutto)
    20, // 📐 Wysokość bloczka (cm)
    153.75 // 🎨 Cena bloczka dekoracyjnego (zł brutto)
  )
]

// 🎯 FUNKCJA: Pobieranie konfiguracji po ID
export const getFenceConfig = (fenceId: string): FenceConfig | undefined => {
  return fenceCatalog.find(f => f.id === fenceId)
}

// 🚀 CACHE dla konfiguracji - eliminuje powtórne requesty HTTP
const configCache = new Map<string, FenceConfig>()
const gateDoorConfigCache: { config: any | null; loaded: boolean } = { config: null, loaded: false }

// 🎯 FUNKCJA: Pobieranie konfiguracji po ID i kolorze
export const getFenceConfigWithColor = async (fenceId: string, color?: string): Promise<FenceConfig | undefined> => {
  const cacheKey = `${fenceId}_${color || 'default'}`
  
  // 🚀 SPRAWDŹ CACHE PRZED ŁADOWANIEM
  const cached = configCache.get(cacheKey)
  if (cached) {
    console.log(`🚀 CONFIG CACHE HIT: ${cacheKey}`)
    return cached
  }
  
  console.log('🔄 DEBUG: getFenceConfigWithColor - rozpoczynam ładowanie dla:', fenceId, 'kolor:', color)
  
  try {
    // 🎯 ŁADUJ KONFIGURACJĘ JSON z fence-configs
    const response = await fetch(`/fence-configs/${fenceId}.json`)
    if (response.ok) {
      const config: FenceConfig = await response.json()
      console.log('✅ DEBUG: getFenceConfigWithColor - załadowano z JSON:', config.name, 'colorTextures:', config.colorTextures)
      
      // 🚪 USUŃ LOKALNE DEFINICJE GATE/DOOR (jeśli istnieją) - używamy tylko globalnego config
      if (config.elements.gate) {
        delete config.elements.gate
      }
      if (config.elements.door) {
        delete config.elements.door
      }
      
      // 🚪 ŁADUJ GLOBALNĄ KONFIGURACJĘ GATE/DOOR (z cache)
      try {
        let gateDoorConfig = gateDoorConfigCache.config
        if (!gateDoorConfigCache.loaded) {
          const gateDoorResponse = await fetch('/fence-configs/gate-door-config.json')
          if (gateDoorResponse.ok) {
            gateDoorConfig = await gateDoorResponse.json()
            gateDoorConfigCache.config = gateDoorConfig
            gateDoorConfigCache.loaded = true
            console.log('🚀 GATE-DOOR CONFIG LOADED & CACHED')
          }
        } else {
          console.log('🚀 GATE-DOOR CONFIG CACHE HIT')
        }
        if (gateDoorConfig && gateDoorConfig.elements) {
          // 🚪 Dołącz gate i door z globalnej konfiguracji
          // DOŁĄCZ modelPath i texturePath TYLKO do gate i door, NIE nadpisuj głównego configu!
          if (gateDoorConfig.elements.gate) {
            config.elements.gate = { ...gateDoorConfig.elements.gate } as any
            console.log('🚪 Gate config loaded:', JSON.stringify(config.elements.gate, null, 2))
            const gateConfig = config.elements.gate as any
            if (gateDoorConfig.modelPath && !gateConfig.modelPath) {
              gateConfig.modelPath = gateDoorConfig.modelPath
            }
            if (gateDoorConfig.texturePath && !gateConfig.texturePath) {
              gateConfig.texturePath = gateDoorConfig.texturePath
            }
            // 🔁 Per-fence overrides
            if (gateDoorConfig.overrides && gateDoorConfig.overrides[fenceId]?.gate) {
              const o = gateDoorConfig.overrides[fenceId].gate
              gateConfig.rightOffset = o.rightOffset ?? gateConfig.rightOffset
              gateConfig.coefficient = o.coefficient ?? gateConfig.coefficient
              gateConfig.spacing = o.spacing ?? gateConfig.spacing
            }
          }
          if (gateDoorConfig.elements.door) {
            config.elements.door = { ...gateDoorConfig.elements.door } as any
            const doorConfig = config.elements.door as any
            if (gateDoorConfig.modelPath && !doorConfig.modelPath) {
              doorConfig.modelPath = gateDoorConfig.modelPath
            }
            if (gateDoorConfig.texturePath && !doorConfig.texturePath) {
              doorConfig.texturePath = gateDoorConfig.texturePath
            }
            // 🔁 Per-fence overrides
            if (gateDoorConfig.overrides && gateDoorConfig.overrides[fenceId]?.door) {
              const o = gateDoorConfig.overrides[fenceId].door
              doorConfig.rightOffset = o.rightOffset ?? doorConfig.rightOffset
              doorConfig.coefficient = o.coefficient ?? doorConfig.coefficient
              doorConfig.spacing = o.spacing ?? doorConfig.spacing
            }
          }
          console.log('✅ Załadowano globalną konfigurację gate/door')
        }
      } catch (gateDoorError) {
        console.warn('⚠️ Nie można załadować konfiguracji gate/door:', gateDoorError)
      }
      
      // 🎯 NOWE: Aktualizuj texturePath dla wybranego koloru
      if (color && config.colorTextures && config.colorTextures[color]) {
        const colorConfig = config.colorTextures[color]
        console.log(`🎨 getFenceConfigWithColor: colorTextures[${color}] =`, {
          hasModelPath: !!colorConfig.modelPath,
          modelPath: colorConfig.modelPath,
          hasMaterialTexture: !!colorConfig.materialTexture,
          materialTexture: colorConfig.materialTexture
        })
        
        // 🎯 AKTUALIZUJ modelPath jeśli kolor ma własny model DAE
        if (colorConfig.modelPath) {
          const oldModelPath = config.modelPath
          config.modelPath = colorConfig.modelPath
          console.log(`🎯 getFenceConfigWithColor: Aktualizuję modelPath dla koloru ${color} z: ${oldModelPath} na: ${colorConfig.modelPath}`)
        }
        
        if (colorConfig.materialTexture) {
          // Jeśli materialTexture to tablica (np. A6 Marago z losowymi wariantami),
          // użyj pierwszego elementu jako texturePath (texturePath musi być stringiem)
          config.texturePath = Array.isArray(colorConfig.materialTexture) 
            ? colorConfig.materialTexture[0] 
            : colorConfig.materialTexture
          console.log('🎯 DEBUG: getFenceConfigWithColor - zaktualizowano texturePath dla koloru:', color, 'na:', config.texturePath)
        }
      } else {
        console.log(`🎨 getFenceConfigWithColor: Brak colorTextures dla koloru ${color || 'default'}, używam głównego modelPath: ${config.modelPath}`)
      }
      
      console.log(`📋 getFenceConfigWithColor: FINAL modelPath=${config.modelPath}`)
      
      // 🚀 ZAPISZ DO CACHE przed returnem
      configCache.set(cacheKey, config)
      console.log(`🚀 CONFIG CACHED: ${cacheKey}`)
      
      return config
    } else {
      console.log('❌ DEBUG: getFenceConfigWithColor - nie można załadować JSON:', fenceId, 'status:', response.status)
      // Fallback do hardcoded konfiguracji
      const config = getFenceConfig(fenceId)
      if (config) {
        console.log('🔄 DEBUG: getFenceConfigWithColor - używam hardcoded konfiguracji dla:', fenceId)
        return config
      }
    }
  } catch (error) {
    console.error('❌ DEBUG: getFenceConfigWithColor - błąd ładowania JSON:', error)
    // Fallback do hardcoded konfiguracji
    const config = getFenceConfig(fenceId)
    if (config) {
      console.log('🔄 DEBUG: getFenceConfigWithColor - używam hardcoded konfiguracji po błędzie dla:', fenceId)
      return config
    }
  }
  
  return undefined
}

// 🎯 FUNKCJA: Ładowanie wszystkich konfiguracji z plików JSON
// 🎯 DEBUG: Wymuszenie odświeżenia cache - timestamp: 1760310441330
export const getAllFenceConfigs = async (): Promise<FenceConfig[]> => {
  console.log('🔄 DEBUG: getAllFenceConfigs - rozpoczynam ładowanie z plików JSON...')
  const fenceIds = [
    'gladkie_max',
    'gladkie_long',
    'gladkie_medium',
    'slim',
    'slim-3d',
    'royal_s50',
    'royal_p50',
    'royal-mix', // 🎯 Royal Mix (S50 / P50)
    'vision-bet-long',
    'vision-bet-max',
    'vision-bet-medium',
    'lupane-muro-p22',
    'lupane-muro-s22',
    'lupane-muro-p38',
    'lupane-muro-s38',
    'lupane-muro-full', // 🎯 Dodaj "Pełne" do listy
    'lupane-muro-mix' // 🎯 Muro Mix (P22 / P38 / S22 / S38)
  ]
  
  const configs: FenceConfig[] = []
  
  // 🚪 ŁADUJ GLOBALNĄ KONFIGURACJĘ GATE/DOOR
  let globalGateDoorConfig: any = null
  try {
    const gateDoorResponse = await fetch('/fence-configs/gate-door-config.json')
    if (gateDoorResponse.ok) {
      const gateDoorData = await gateDoorResponse.json()
      globalGateDoorConfig = gateDoorData.elements
      console.log('✅ Załadowano globalną konfigurację gate/door dla wszystkich ogrodzeń')
    }
  } catch (gateDoorError) {
    console.warn('⚠️ Nie można załadować konfiguracji gate/door:', gateDoorError)
  }
  
  for (const fenceId of fenceIds) {
    console.log(`🔄 DEBUG: Ładuję konfigurację JSON: ${fenceId}`)
    try {
      const response = await fetch(`/fence-configs/${fenceId}.json`)
      if (response.ok) {
        const config: FenceConfig = await response.json()
        console.log(`✅ DEBUG: Załadowano z JSON: ${config.name} (${config.category})`)
        
        // 🐛 DEBUG: Dla Vision Bet Long sprawdź colorTextures
        if (fenceId === 'vision-bet-long') {
          console.log(`🔍 Vision Bet Long - colorTextures keys:`, config.colorTextures ? Object.keys(config.colorTextures).length : 0)
          console.log(`🔍 Vision Bet Long - kolor 01:`, !!config.colorTextures?.['01'])
          console.log(`🔍 Vision Bet Long - kolor 06:`, !!config.colorTextures?.['06'])
          console.log(`🔍 Vision Bet Long - kolor 07:`, !!config.colorTextures?.['07'])
        }
        
        // 🚪 USUŃ LOKALNE DEFINICJE GATE/DOOR (jeśli istnieją) - używamy tylko globalnego config
        if (config.elements.gate) {
          delete config.elements.gate
        }
        if (config.elements.door) {
          delete config.elements.door
        }
        
        // 🚪 Dołącz gate i door z globalnej konfiguracji
        if (globalGateDoorConfig) {
          // Dołącz TYLKO elementy gate/door, NIE nadpisuj głównego configu!
          config.elements.gate = globalGateDoorConfig.gate
          config.elements.door = globalGateDoorConfig.door
          // 🔁 Per-fence overrides (jeśli istnieją)
          try {
            const respOver = await fetch('/fence-configs/gate-door-config.json')
            if (respOver.ok) {
              const g = await respOver.json()
              if (g.overrides && g.overrides[config.id]) {
                const o = g.overrides[config.id]
                if (o.gate && config.elements.gate) {
                  const gateConfig = config.elements.gate as any
                  gateConfig.rightOffset = o.gate.rightOffset ?? gateConfig.rightOffset
                  gateConfig.coefficient = o.gate.coefficient ?? gateConfig.coefficient
                  gateConfig.spacing = o.gate.spacing ?? gateConfig.spacing
                }
                if (o.door && config.elements.door) {
                  const doorConfig = config.elements.door as any
                  doorConfig.rightOffset = o.door.rightOffset ?? doorConfig.rightOffset
                  doorConfig.coefficient = o.door.coefficient ?? doorConfig.coefficient
                  doorConfig.spacing = o.door.spacing ?? doorConfig.spacing
                }
              }
            }
          } catch {}
        }
        
        configs.push(config)
      } else {
        console.log(`❌ DEBUG: Nie można załadować JSON: ${fenceId}.json (${response.status})`)
        // Fallback do hardcoded konfiguracji
        const hardcodedConfig = getFenceConfig(fenceId)
        if (hardcodedConfig) {
          console.log(`🔄 DEBUG: Używam hardcoded konfiguracji dla: ${fenceId}`)
          configs.push(hardcodedConfig)
        }
      }
    } catch (error) {
      console.error(`❌ DEBUG: Błąd ładowania JSON ${fenceId}:`, error)
      // Fallback do hardcoded konfiguracji
      const hardcodedConfig = getFenceConfig(fenceId)
      if (hardcodedConfig) {
        console.log(`🔄 DEBUG: Używam hardcoded konfiguracji dla: ${fenceId}`)
        configs.push(hardcodedConfig)
      }
    }
  }
  
  console.log(`🎯 DEBUG: getAllFenceConfigs - załadowano ${configs.length} konfiguracji:`, configs.map(c => `${c.name} (${c.category})`))
  return configs
}
